import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { zodFormResolver as zodResolver } from "@/lib/zodFormResolver";
import * as z from "zod";
import { useCreateTemplate, useAiGenerateTemplate, useGetDashboardSummary, useListTemplates, useListSnippets, getGetDashboardSummaryQueryKey, getListTemplatesQueryKey, type CreateTemplateInput, type CreateTemplateInputLayout, type AiGenerateInput, type AiGenerateInputLayoutHint, type AiGenerateInputTone, type DashboardSummary } from "@workspace/api-client-react";
import { VARIABLE_TYPE_VALUES, VARIABLE_TYPE_LABELS, TEMPLATE_LAYOUT_VALUES, LAYOUT_LABELS, AI_LAYOUT_HINT_VALUES, AI_TONE_VALUES, AI_TONE_LABELS } from "@/lib/enumValues";
import { predictSlugAdjustment } from "@/lib/templateSlug";
import { apiErrorMessage, apiErrorStatus, apiErrorRateLimit } from "@/lib/apiError";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, ArrowLeft, Plus, Trash2, Sparkles, Puzzle } from "lucide-react";
import { Link } from "wouter";
import { AiRateLimitInline } from "@/components/AiRateLimitInline";

const variableSchema = z.object({
  key: z.string().min(1, "Key is required"),
  label: z.string().min(1, "Label is required"),
  type: z.enum(VARIABLE_TYPE_VALUES),
  required: z.boolean(),
  defaultValue: z.string().optional().nullable(),
  placeholder: z.string().optional().nullable(),
});

const manualFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(120),
  category: z.string().min(1, "Category is required").max(60),
  layout: z.enum(TEMPLATE_LAYOUT_VALUES),
  description: z.string().max(280),
  subject: z.string().min(1, "Subject is required").max(200),
  previewText: z.string().max(200).optional().nullable(),
  bodyHtml: z.string().min(1, "HTML Body is required"),
  variables: z.array(variableSchema),
  sourcePrompt: z.string().optional().nullable(),
});

const aiFormSchema = z.object({
  prompt: z.string().min(4, "Prompt must be at least 4 characters").max(1000),
  category: z.string().max(60).optional().nullable(),
  layoutHint: z.enum(AI_LAYOUT_HINT_VALUES).optional().nullable(),
  tone: z.enum(AI_TONE_VALUES).optional().nullable(),
});

export default function TemplateNew() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const createMutation = useCreateTemplate();
  const aiGenerateMutation = useAiGenerateTemplate();
  const dashboardSummaryQuery = useGetDashboardSummary();
  const userTemplatesQuery = useListTemplates({ source: "user" });
  const existingUserSlugs = useMemo(
    () => (userTemplatesQuery.data ?? []).map((t) => t.slug),
    [userTemplatesQuery.data],
  );

  const [activeTab, setActiveTab] = useState<string>("manual");
  const [draftMode, setDraftMode] = useState<boolean>(false);

  const aiRateLimitRemaining = dashboardSummaryQuery.data?.aiRateLimitRemaining;
  const aiRateLimitMax = dashboardSummaryQuery.data?.aiRateLimitMax;
  const isAiLimitLoaded =
    typeof aiRateLimitRemaining === "number" &&
    typeof aiRateLimitMax === "number";
  const isAiLimitExhausted = isAiLimitLoaded && aiRateLimitRemaining! <= 0;

  const manualForm = useForm<z.infer<typeof manualFormSchema>>({
    resolver: zodResolver(manualFormSchema),
    defaultValues: {
      name: "",
      category: "",
      layout: "minimal",
      description: "",
      subject: "",
      previewText: "",
      bodyHtml: "",
      variables: [],
      sourcePrompt: "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: manualForm.control,
    name: "variables",
  });

  const snippetsQuery = useListSnippets();
  const [manualSnippetSlug, setManualSnippetSlug] = useState<string>("");
  const insertSnippetIntoBody = (slug: string) => {
    if (!slug) return;
    const current = manualForm.getValues("bodyHtml") ?? "";
    const insertion = `{{> ${slug}}}`;
    const next = current
      ? `${current}${current.endsWith("\n") ? "" : "\n"}${insertion}`
      : insertion;
    manualForm.setValue("bodyHtml", next, { shouldDirty: true, shouldValidate: true });
  };

  const watchedName = useWatch({ control: manualForm.control, name: "name" });
  const slugAdjustment = useMemo(
    () => predictSlugAdjustment(watchedName ?? "", existingUserSlugs),
    [watchedName, existingUserSlugs],
  );

  const aiForm = useForm<z.infer<typeof aiFormSchema>>({
    resolver: zodResolver(aiFormSchema),
    defaultValues: {
      prompt: "",
      category: "",
      layoutHint: null,
      tone: null,
    },
  });

  const onManualSubmit = async (values: z.infer<typeof manualFormSchema>) => {
    try {
      const data: CreateTemplateInput = {
        name: values.name,
        category: values.category,
        description: values.description,
        layout: values.layout as CreateTemplateInputLayout,
        subject: values.subject,
        previewText: values.previewText ?? null,
        bodyHtml: values.bodyHtml,
        variables: values.variables,
        sourcePrompt: values.sourcePrompt ?? null,
      };
      const res = await createMutation.mutateAsync({ data });
      toast.success("Template created");
      queryClient.invalidateQueries({ queryKey: getListTemplatesQueryKey() });
      setLocation(`/app/templates/${res.id}`);
    } catch (e: unknown) {
      toast.error(apiErrorMessage(e, "Failed to create template"));
    }
  };

  const onAiSubmit = async (values: z.infer<typeof aiFormSchema>) => {
    try {
      const data: AiGenerateInput = {
        prompt: values.prompt,
        category: values.category ?? null,
        layoutHint: (values.layoutHint ?? null) as AiGenerateInputLayoutHint | null,
        tone: (values.tone ?? null) as AiGenerateInputTone | null,
      };
      const draft = await aiGenerateMutation.mutateAsync({ data });

      const summaryKey = getGetDashboardSummaryQueryKey();
      const cached = queryClient.getQueryData<DashboardSummary>(summaryKey);
      if (cached) {
        queryClient.setQueryData<DashboardSummary>(summaryKey, {
          ...cached,
          aiRateLimitRemaining: draft.rateLimitRemaining,
          aiRateLimitMax: draft.rateLimitMax,
          aiRateLimitResetAt: draft.rateLimitResetAt,
        });
      } else {
        queryClient.invalidateQueries({ queryKey: summaryKey });
      }

      manualForm.reset({
        name: draft.name,
        category: draft.category,
        layout: draft.layout,
        description: draft.description,
        subject: draft.subject,
        previewText: draft.previewText,
        bodyHtml: draft.bodyHtml,
        variables: draft.variables,
        sourcePrompt: draft.sourcePrompt,
      });

      setDraftMode(true);
      setActiveTab("manual");
      toast.success("AI Generation complete. Please review.");
    } catch (e: unknown) {
      if (apiErrorStatus(e) === 429) {
        const limit = apiErrorRateLimit(e);
        if (limit) {
          const summaryKey = getGetDashboardSummaryQueryKey();
          const cached = queryClient.getQueryData<DashboardSummary>(summaryKey);
          if (cached) {
            queryClient.setQueryData<DashboardSummary>(summaryKey, {
              ...cached,
              aiRateLimitRemaining: limit.remaining,
              aiRateLimitMax: limit.max,
              aiRateLimitResetAt: limit.resetAt,
            });
          } else {
            queryClient.invalidateQueries({ queryKey: summaryKey });
          }
        }
      }
      toast.error(apiErrorMessage(e, "AI generation failed"));
    }
  };

  return (
    <div className="flex-1 overflow-auto p-8 bg-background">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setLocation("/app/templates")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Create Template</h1>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {!draftMode && (
            <TabsList className="mb-4">
              <TabsTrigger value="manual">Manual</TabsTrigger>
              <TabsTrigger value="ai" className="flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                Generate with AI
              </TabsTrigger>
            </TabsList>
          )}

          <TabsContent value="manual" className="m-0 space-y-6">
            {draftMode && (
              <div className="bg-primary/10 border border-primary/20 text-primary px-4 py-3 rounded-lg flex items-start gap-3">
                <Sparkles className="w-5 h-5 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Powered by AI — review before saving.</p>
                  <p className="text-xs opacity-90 mt-0.5">The AI generated this draft based on your prompt. Make any necessary changes below.</p>
                </div>
              </div>
            )}

            <Form {...manualForm}>
              <form onSubmit={manualForm.handleSubmit(onManualSubmit)} className="space-y-8">
                <Card>
                  <CardContent className="pt-6 space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField control={manualForm.control} name="name" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Name</FormLabel>
                          <FormControl><Input {...field} /></FormControl>
                          {slugAdjustment && (
                            <p
                              className="text-xs text-blue-700 dark:text-blue-300 mt-1"
                              data-testid="slug-adjustment-hint"
                            >
                              {slugAdjustment.reason === "reserved"
                                ? "This name is reserved — your URL will be adjusted to "
                                : "You already have a template with this name — your URL will be adjusted to "}
                              <code className="font-mono text-[11px] bg-blue-500/15 px-1 py-0.5 rounded">
                                {slugAdjustment.baseSlug}-…
                              </code>
                              {" "}so it stays unique. The template name itself is unchanged.
                            </p>
                          )}
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={manualForm.control} name="category" render={({ field }) => (
                        <FormItem><FormLabel>Category</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                    </div>

                    <FormField control={manualForm.control} name="description" render={({ field }) => (
                      <FormItem><FormLabel>Description</FormLabel><FormControl><Textarea rows={2} {...field} /></FormControl><FormMessage /></FormItem>
                    )} />

                    <div className="grid grid-cols-2 gap-4">
                      <FormField control={manualForm.control} name="subject" render={({ field }) => (
                        <FormItem><FormLabel>Subject</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={manualForm.control} name="previewText" render={({ field }) => (
                        <FormItem><FormLabel>Preview Text (optional)</FormLabel><FormControl><Input {...field} value={field.value || ""} /></FormControl><FormMessage /></FormItem>
                      )} />
                    </div>

                    <FormField control={manualForm.control} name="layout" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Layout Wrapper</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl><SelectTrigger><SelectValue placeholder="Select layout" /></SelectTrigger></FormControl>
                          <SelectContent>
                            {TEMPLATE_LAYOUT_VALUES.map((v) => (
                              <SelectItem key={v} value={v}>{LAYOUT_LABELS[v]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />

                    <FormField control={manualForm.control} name="bodyHtml" render={({ field }) => (
                      <FormItem>
                        <FormLabel>HTML Body</FormLabel>
                        <FormDescription>Use {"{{variable_key}}"} for dynamic insertions.</FormDescription>
                        <FormControl><Textarea rows={10} className="font-mono text-xs" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />

                    <div className="space-y-1.5" data-testid="snippet-picker-new">
                      <label className="text-sm font-medium flex items-center gap-1.5">
                        <Puzzle className="w-3.5 h-3.5" />
                        Insert Snippet
                      </label>
                      <div className="flex items-center gap-2">
                        <Select
                          value={manualSnippetSlug}
                          onValueChange={(v) => {
                            setManualSnippetSlug(v);
                            insertSnippetIntoBody(v);
                            setManualSnippetSlug("");
                          }}
                        >
                          <SelectTrigger
                            className="h-8 text-xs flex-1"
                            aria-label="Insert snippet"
                            data-testid="select-insert-snippet-new"
                          >
                            <SelectValue placeholder="Pick a snippet to append…" />
                          </SelectTrigger>
                          <SelectContent>
                            {(snippetsQuery.data ?? []).length === 0 ? (
                              <div className="px-2 py-1.5 text-xs text-muted-foreground">No snippets yet</div>
                            ) : (
                              (snippetsQuery.data ?? []).map((s) => (
                                <SelectItem key={s.id} value={s.slug}>
                                  <span className="truncate">{s.name}</span>
                                  {s.isBuiltin && (
                                    <span className="ml-2 text-[10px] text-muted-foreground">built-in</span>
                                  )}
                                  <code className="ml-2 text-[10px] text-muted-foreground">{s.slug}</code>
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                        <Button asChild type="button" variant="ghost" size="sm" className="h-8 text-xs">
                          <Link href="/app/snippets">Manage</Link>
                        </Button>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Appends <code>{`{{> slug}}`}</code> to the body. You can also type the syntax manually.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium">Variables</h3>
                      <Button type="button" variant="outline" size="sm" onClick={() => append({ key: "", label: "", type: "text", required: true, defaultValue: "", placeholder: "" })}>
                        <Plus className="w-4 h-4 mr-2" /> Add Variable
                      </Button>
                    </div>
                    
                    <div className="space-y-4">
                      {fields.map((field, index) => (
                        <div key={field.id} className="grid grid-cols-[1fr_1fr_120px_auto_auto] gap-3 items-start border border-border p-3 rounded-md">
                          <FormField control={manualForm.control} name={`variables.${index}.key`} render={({ field }) => (
                            <FormItem><FormLabel className="text-xs">Key</FormLabel><FormControl><Input size={1} className="h-8 text-xs" placeholder="e.g. first_name" {...field} /></FormControl><FormMessage /></FormItem>
                          )} />
                          <FormField control={manualForm.control} name={`variables.${index}.label`} render={({ field }) => (
                            <FormItem><FormLabel className="text-xs">Label</FormLabel><FormControl><Input size={1} className="h-8 text-xs" placeholder="e.g. First Name" {...field} /></FormControl><FormMessage /></FormItem>
                          )} />
                          <FormField control={manualForm.control} name={`variables.${index}.type`} render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs">Type</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl><SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger></FormControl>
                                <SelectContent>
                                  {VARIABLE_TYPE_VALUES.map((v) => (
                                    <SelectItem key={v} value={v}>{VARIABLE_TYPE_LABELS[v]}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormItem>
                          )} />
                          <FormField control={manualForm.control} name={`variables.${index}.required`} render={({ field }) => (
                            <FormItem className="flex flex-col items-center justify-center pt-5">
                              <FormLabel className="text-[10px] uppercase mb-1">Req</FormLabel>
                              <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                            </FormItem>
                          )} />
                          <div className="pt-6">
                            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(index)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {fields.length === 0 && (
                        <div className="text-center text-sm text-muted-foreground py-4 border border-dashed rounded-md">
                          No variables defined.
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <div className="flex justify-end gap-4">
                  <Button type="button" variant="outline" onClick={() => setLocation("/app/templates")} disabled={createMutation.isPending}>Cancel</Button>
                  <Button type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save Template
                  </Button>
                </div>
              </form>
            </Form>
          </TabsContent>

          <TabsContent value="ai" className="m-0 space-y-4">
            <AiRateLimitInline />


            <Card>
              <CardContent className="pt-6">
                <Form {...aiForm}>
                  <form onSubmit={aiForm.handleSubmit(onAiSubmit)} className="space-y-6">
                    <FormField control={aiForm.control} name="prompt" render={({ field }) => (
                      <FormItem>
                        <FormLabel>What do you need?</FormLabel>
                        <FormControl>
                          <Textarea rows={4} placeholder="e.g. A welcome email for new signups to our project management tool. Should include a button to 'Get Started' and a link to our documentation." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />

                    <div className="grid grid-cols-3 gap-4">
                      <FormField control={aiForm.control} name="category" render={({ field }) => (
                        <FormItem><FormLabel>Category (opt)</FormLabel><FormControl><Input placeholder="e.g. Onboarding" {...field} value={field.value || ""} /></FormControl><FormMessage /></FormItem>
                      )} />
                      
                      <FormField control={aiForm.control} name="layoutHint" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Layout Hint (opt)</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value || ""}>
                            <FormControl><SelectTrigger><SelectValue placeholder="Auto" /></SelectTrigger></FormControl>
                            <SelectContent>
                              {AI_LAYOUT_HINT_VALUES.map((v) => (
                                <SelectItem key={v} value={v}>{LAYOUT_LABELS[v]}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />

                      <FormField control={aiForm.control} name="tone" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tone (opt)</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value || ""}>
                            <FormControl><SelectTrigger><SelectValue placeholder="Auto" /></SelectTrigger></FormControl>
                            <SelectContent>
                              {AI_TONE_VALUES.map((v) => (
                                <SelectItem key={v} value={v}>{AI_TONE_LABELS[v]}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>

                    <div className="flex justify-end">
                      <Button type="submit" disabled={aiGenerateMutation.isPending || isAiLimitExhausted} className="bg-gradient-to-r from-primary to-indigo-500 text-white border-0">
                        {aiGenerateMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                        Generate Template
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
